/**
 * React Query key constants
 * Used for caching and invalidating queries
 */
export const QUERY_KEYS = {
  USERS: "users",
  USER_ADDRESSES: "user-addresses",
  USER_PETS: "user-pets",
  USER_PET: "user-pet",
  PET_BREEDS: "pet-breeds",
  MEMBERS: "members",
  PETS: "pets",
  SUBSCRIPTIONS: "subscriptions",
  BULK_RESCHEDULE_PREVIEW: "bulk-reschedule-preview",
  DISCOUNTS: "discounts",
  SESSIONS: "sessions",
  BANNERS: "banners",
  ORDERS: "orders",
  PARTNERS: "partners",
  PARTNER_LEAVES: "partner-leaves",
  PARTNER_WORKING_HOURS: "partner-working-hours",
  PARTNER_ONBOARDING: "partner-onboarding",
  PARTNER_ONBOARDING_ADMIN_DETAILS: "partner-onboarding-admin-details",
  PARTNER_REVIEWS: "partner-reviews",
  PARTNER_REVIEW_STATS: "partner-review-stats",
  PARTNER_PERFORMANCE: "partner-performance",
  PARTNER_ORDERS: "partner-orders",
  CITIES: "cities",
  SUB_CITIES: "sub-cities",
  HUBS: "hubs",
  HOTSPOTS: "hotspots",
  SESSION_ASSIGNMENT_REQUESTS: "session-assignment-requests",
  SESSION_CHANGE_REQUESTS: "session-change-requests",
  SESSION_CHANGE_OPTIONS: "session-change-options",
  ADMIN_TASKS: "admin-tasks",
  GEO_ANALYTICS: "geo-analytics",
  ADMIN_ORDERS: "admin-orders",
  ADMIN_ORDER: "admin-order",
  USER_ORDER: "user-order",
  ORDER_COIN_LEDGER: "order-coin-ledger",
  USER_ORDER_CANCELLABLE_ITEMS: "user-order-cancellable-items",
  USER_ORDER_PAYMENT: "user-order-payment",
  USER_COIN_BALANCE: "user-coin-balance",
  CUSTOMER_COIN_BALANCE: "customer-coin-balance",
  CUSTOMER_COIN_TRANSACTIONS: "customer-coin-transactions",
  SERVICE_AVAILABILITY: "service-availability",
  TIME_SLOTS: "time-slots",
  TIME_SLOTS_SESSIONS: "time-slots-sessions",
  PARTNER_SMS_BLOCK_STATUS: "partner-sms-block-status",
  SUPPORT_TICKETS: "support-tickets",
  SUPPORT_TICKET: "support-ticket",
  SUPPORT_TICKET_EVENTS: "support-ticket-events",
  SUPPORT_TICKET_FORM_CONFIG: "support-ticket-form-config",
  SUPPORT_CONTACT_SEARCH: "support-contact-search",
  SUPPORT_ANALYTICS_DASHBOARD: "support-analytics-dashboard",
};

/**
 * Common pagination constants
 * Used across all API calls for consistent pagination
 */
export const PAGINATION_CONSTANTS = {
  DEFAULT_PAGE: 1,
  DEFAULT_LIMIT: 50,
  DEFAULT_PAGE_SIZE: 10,
  MAX_LIMIT: 100,
} as const;

/**
 * Helper functions to generate query keys with proper structure
 */
export const getUserQueryKey = (id?: string) =>
  id ? [QUERY_KEYS.USERS, id] : [QUERY_KEYS.USERS];

export const getUsersListQueryKey = (filters: { page: number; limit: number; appliedSearch: string }) =>
  [QUERY_KEYS.USERS, "list", filters] as const;

export const getUserAddressesQueryKey = (userId: string, page: number, limit: number) =>
  [QUERY_KEYS.USER_ADDRESSES, userId, page, limit] as const;

export const getUserCoinBalanceQueryKey = (userId: string) =>
  [QUERY_KEYS.USER_COIN_BALANCE, userId] as const;

export const getCustomerCoinBalanceQueryKey = (userId: string) =>
  [QUERY_KEYS.CUSTOMER_COIN_BALANCE, userId] as const;

export const getCustomerCoinTransactionsQueryKey = (userId: string, params?: unknown) =>
  [QUERY_KEYS.CUSTOMER_COIN_TRANSACTIONS, userId, params] as const;

export const getUserPetsQueryKey = (userId: string) => [QUERY_KEYS.USER_PETS, userId] as const;

export const getUserPetQueryKey = (userId: string, petId: string) =>
  [QUERY_KEYS.USER_PET, userId, petId] as const;

export const getMemberQueryKey = (id?: string) =>
  id ? [QUERY_KEYS.MEMBERS, id] : [QUERY_KEYS.MEMBERS];

export const getPetQueryKey = (id?: string) =>
  id ? [QUERY_KEYS.PETS, id] : [QUERY_KEYS.PETS];

/**
 * Cache configuration constants for React Query
 * Different data types have different caching requirements
 */
export const CACHE_CONFIG = {
  // Fast-changing data (search results, live data)
  SHORT: {
    staleTime: 1 * 60 * 1000,   // 1 minute
    gcTime: 5 * 60 * 1000,      // 5 minutes
  },

  // Regular data (user content, moderate frequency updates)
  MEDIUM: {
    staleTime: 5 * 60 * 1000,   // 5 minutes
    gcTime: 10 * 60 * 1000,     // 10 minutes
  },

  // Stable data (configuration, settings, rarely changing)
  LONG: {
    staleTime: 10 * 60 * 1000,  // 10 minutes
    gcTime: 30 * 60 * 1000,     // 30 minutes
  },

  // Very stable data (static content, reference data)
  EXTENDED: {
    staleTime: 30 * 60 * 1000,  // 30 minutes
    gcTime: 60 * 60 * 1000,     // 1 hour
  },

  // Metadata (configuration files, rarely changing)
  METADATA: {
    staleTime: 60 * 60 * 1000,  // 1 hour
    gcTime: 24 * 60 * 60 * 1000, // 24 hours
  },
} as const;

/**
 * Specific cache configurations for different data types
 */
export const DATA_CACHE_CONFIG = {
  // User-generated content (blogs, posts, comments)
  USER_CONTENT: CACHE_CONFIG.MEDIUM,

  // Search and filtering results
  SEARCH_RESULTS: CACHE_CONFIG.SHORT,

  // User profiles and account data
  USER_DATA: CACHE_CONFIG.MEDIUM,

  // Reference data (breeds, categories, tags)
  REFERENCE_DATA: CACHE_CONFIG.LONG,

  // Configuration and settings
  SETTINGS: CACHE_CONFIG.EXTENDED,

  // Analytics and statistics
  ANALYTICS: CACHE_CONFIG.LONG,

  // Product and subscription data
  PRODUCTS: CACHE_CONFIG.MEDIUM,

  // Metadata (configuration files, rarely changing)
  METADATA: CACHE_CONFIG.METADATA,
} as const;

export const getSupportTicketsQueryKey = (params?: unknown) =>
  [QUERY_KEYS.SUPPORT_TICKETS, params] as const;

export const getSupportTicketQueryKey = (idOrNumber: string) =>
  [QUERY_KEYS.SUPPORT_TICKET, idOrNumber] as const;

export const getSupportTicketEventsQueryKey = (idOrNumber: string, params?: unknown) =>
  [QUERY_KEYS.SUPPORT_TICKET_EVENTS, idOrNumber, params] as const;

export const getSupportTicketFormConfigQueryKey = () =>
  [QUERY_KEYS.SUPPORT_TICKET_FORM_CONFIG] as const;

export const getSupportContactSearchQueryKey = (query: string, ticketType?: string) =>
  [QUERY_KEYS.SUPPORT_CONTACT_SEARCH, query, ticketType] as const;

export const getSupportAnalyticsDashboardQueryKey = () =>
  [QUERY_KEYS.SUPPORT_ANALYTICS_DASHBOARD] as const;
